import unified from 'unified';
import remarkParse from 'remark-parse';

/**
 * Strip markdown formatting from text and return plain text.
 * This is useful for social media meta tags where we want clean text
 * without markdown syntax.
 * 
 * @param {string} markdownText - The markdown text to strip
 * @returns {string} Plain text without markdown formatting
 */
export const stripMarkdown = (markdownText) => {
  if (!markdownText || typeof markdownText !== 'string') {
    return markdownText || '';
  }

  try {
    // Parse markdown to AST
    const processor = unified().use(remarkParse);
    const tree = processor.parse(markdownText);

    // Extract text content from AST nodes
    const extractText = (node) => {
      if (node.type === 'text') {
        return node.value;
      }

      if (node.type === 'inlineCode' || node.type === 'code') {
        return node.value;
      }

      // For block-level elements, add spaces between them
      if (node.children && Array.isArray(node.children)) {
        const isBlockLevel = ['paragraph', 'heading', 'blockquote', 'listItem'].includes(node.type);
        const childTexts = node.children.map(extractText);
        return isBlockLevel ? childTexts.join(' ') : childTexts.join('');
      }

      return '';
    };

    // For root node, join children with spaces to separate block elements
    const childTexts = tree.children ? tree.children.map(extractText) : [extractText(tree)];
    const plainText = childTexts
      .filter(text => text.trim().length > 0)
      .join(' ')
      .replace(/\s+/g, ' ') // Replace multiple spaces/newlines with single space
      .trim();

    return plainText;
  } catch (error) {
    // Fallback: use simple regex-based stripping if parsing fails
    return markdownText
      .replace(/!\[.*?\]\(.*?\)/g, '') // Remove images
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Replace links with text
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/`(.*?)`/g, '$1') // Remove inline code
      .replace(/#{1,6}\s+/g, '') // Remove headers
      .replace(/>\s+/g, '') // Remove blockquotes
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
  }
};